import { useState, forwardRef } from 'react';
import { Mic, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface IdeaInputProps {
  onSubmit: (text: string) => Promise<void>;
  disabled?: boolean;
}

const placeholders = [
  'An idea you don\'t want to lose…',
  'Something that just crossed your mind…',
  'A thought worth keeping…',
  'Say it before it fades…',
];

export const IdeaInput = forwardRef<HTMLTextAreaElement, IdeaInputProps>(
  ({ onSubmit, disabled }, ref) => {
    const [value, setValue] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [placeholder] = useState(
      () => placeholders[Math.floor(Math.random() * placeholders.length)]
    );

    const handleSubmit = async () => {
      if (!value.trim() || isSubmitting || disabled) return;
      
      setIsSubmitting(true);
      try {
        await onSubmit(value.trim());
        setValue('');
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    return (
      <div className="w-full max-w-xl mx-auto">
        <div className="relative">
          <Textarea
            ref={ref}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSubmitting}
            className={cn(
              'min-h-[120px] resize-none pr-12',
              'bg-card/80 backdrop-blur-sm',
              'border border-secondary/40 rounded-2xl',
              'text-base leading-relaxed',
              'placeholder:text-muted-foreground/40 placeholder:font-light',
              'focus:border-secondary/60 focus:ring-0 focus:ring-offset-0',
              'focus:shadow-[inset_0_1px_12px_0_hsl(var(--secondary)/0.08)]',
              'transition-all duration-300 ease-out'
            )}
          />
          
          <div className="absolute bottom-3 right-3">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              disabled
              className="h-7 w-7 text-muted-foreground/30"
              title="Voice input (coming soon)"
            >
              <Mic className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        
        <div className="mt-6 flex flex-col items-center gap-2">
          <Button
            onClick={handleSubmit}
            disabled={!value.trim() || isSubmitting || disabled}
            variant="secondary"
            className={cn(
              'px-6 py-2 h-auto rounded-full',
              'text-sm font-normal',
              'bg-primary/80 hover:bg-primary/90 text-primary-foreground',
              'disabled:opacity-40',
              'transition-all duration-200'
            )}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Planting…
              </>
            ) : (
              'Plant Seed'
            )}
          </Button>
          <span className="text-[11px] text-muted-foreground/40 font-light">
            You can refine it later.
          </span>
        </div>
      </div>
    );
  }
);

IdeaInput.displayName = 'IdeaInput';
